import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Footer from '@/components/Footer';
import {
  BookOpen,
  BarChart3,
  Clock,
  Star,
  Bookmark,
  Users,
  ArrowRight,
  Shield,
} from 'lucide-react';

interface SignUpBenefitsProps {
  onSignUp: () => void;
  onSignIn: () => void;
}

const SignUpBenefits = ({ onSignUp, onSignIn }: SignUpBenefitsProps) => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-learning-muted/20 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-hero text-primary-foreground">
        <div className="container max-w-6xl mx-auto px-6 py-16">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <BookOpen className="h-12 w-12" />
              <span className="text-4xl font-bold">Lingua Craft</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {t('signUpBenefits.title')}
            </h1>
            <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto mb-8">
              {t('signUpBenefits.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={onSignUp}
                size="lg"
                className="bg-white/20 hover:bg-white/30 text-primary-foreground border-white/30 px-8 py-4 text-lg"
              >
                {t('signUpBenefits.startLearningFree')}{' '}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                onClick={onSignIn}
                variant="outline"
                size="lg"
                className="bg-transparent border-white/30 text-primary-foreground hover:bg-white/10 px-8 py-4 text-lg"
              >
                {t('auth.signIn')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="container max-w-6xl mx-auto px-6 py-16 flex-grow">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">
            {t('signUpBenefits.whySignUp')}
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('signUpBenefits.unlockFeatures')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {/* Progress Tracking */}
          <Card className="bg-white/70 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <BarChart3 className="h-8 w-8 text-primary" />
                <h3 className="text-xl font-semibold">
                  {t('signUpBenefits.trackProgress.title')}
                </h3>
              </div>
              <p className="text-muted-foreground mb-4">
                {t('signUpBenefits.trackProgress.description')}
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                {(
                  t('signUpBenefits.trackProgress.features', {
                    returnObjects: true,
                  }) as string[]
                ).map((feature: string, index: number) => (
                  <li key={index}>• {feature}</li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Reading History */}
          <Card className="bg-white/70 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="h-8 w-8 text-primary" />
                <h3 className="text-xl font-semibold">
                  {t('signUpBenefits.readingHistory.title')}
                </h3>
              </div>
              <p className="text-muted-foreground mb-4">
                {t('signUpBenefits.readingHistory.description')}
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                {(
                  t('signUpBenefits.readingHistory.features', {
                    returnObjects: true,
                  }) as string[]
                ).map((feature: string, index: number) => (
                  <li key={index}>• {feature}</li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Personalized Experience */}
          <Card className="bg-white/70 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Star className="h-8 w-8 text-primary" />
                <h3 className="text-xl font-semibold">
                  {t('signUpBenefits.personalizedLearning.title')}
                </h3>
              </div>
              <p className="text-muted-foreground mb-4">
                {t('signUpBenefits.personalizedLearning.description')}
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                {(
                  t('signUpBenefits.personalizedLearning.features', {
                    returnObjects: true,
                  }) as string[]
                ).map((feature: string, index: number) => (
                  <li key={index}>• {feature}</li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Bookmarks & Favorites */}
          <Card className="bg-white/70 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Bookmark className="h-8 w-8 text-primary" />
                <h3 className="text-xl font-semibold">
                  {t('signUpBenefits.saveOrganize.title')}
                </h3>
              </div>
              <p className="text-muted-foreground mb-4">
                {t('signUpBenefits.saveOrganize.description')}
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                {(
                  t('signUpBenefits.saveOrganize.features', {
                    returnObjects: true,
                  }) as string[]
                ).map((feature: string, index: number) => (
                  <li key={index}>• {feature}</li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Community Features */}
          <Card className="bg-white/70 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Users className="h-8 w-8 text-primary" />
                <h3 className="text-xl font-semibold">
                  {t('signUpBenefits.learnTogether.title')}
                </h3>
              </div>
              <p className="text-muted-foreground mb-4">
                {t('signUpBenefits.learnTogether.description')}
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                {(
                  t('signUpBenefits.learnTogether.features', {
                    returnObjects: true,
                  }) as string[]
                ).map((feature: string, index: number) => (
                  <li key={index}>• {feature}</li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Secure & Private */}
          <Card className="bg-white/70 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="h-8 w-8 text-primary" />
                <h3 className="text-xl font-semibold">
                  {t('signUpBenefits.securePrivate.title')}
                </h3>
              </div>
              <p className="text-muted-foreground mb-4">
                {t('signUpBenefits.securePrivate.description')}
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                {(
                  t('signUpBenefits.securePrivate.features', {
                    returnObjects: true,
                  }) as string[]
                ).map((feature: string, index: number) => (
                  <li key={index}>• {feature}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Call to Action */}
        <div className="text-center bg-gradient-hero rounded-2xl p-8 text-primary-foreground">
          <h2 className="text-3xl font-bold mb-4">
            {t('signUpBenefits.readyToBegin')}
          </h2>
          <p className="text-xl mb-6 text-primary-foreground/90">
            {t('signUpBenefits.joinThousands')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={onSignUp}
              size="lg"
              className="bg-white text-primary hover:bg-white/90 px-8 py-4 text-lg font-semibold"
            >
              {t('signUpBenefits.createFreeAccount')}{' '}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              onClick={onSignIn}
              variant="outline"
              size="lg"
              className="bg-transparent border-white/30 text-primary-foreground hover:bg-white/10 px-8 py-4 text-lg"
            >
              {t('auth.alreadyHaveAccount')}
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SignUpBenefits;
